<template>
    <div class="flex flex-col gap-4">
        <div>
            <vx-label>Conversion Type</vx-label>
            <vx-selectable
                data-test="conversion-type"
                v-model="data.conversion_type"
                :options="typeOptions"
                :reduce="(option) => option.value"
                :clearable="false"
                size="lg"
                placeholder="Select type"
                :error="displayFirstError(validate.conversion_type)"
            />
        </div>

        <div>
            <vx-label>Conversion Subtype</vx-label>
            <vx-selectable
                data-test="conversion-subtype"
                v-model="data.conversion_subtype"
                :options="subtypeOptions"
                :reduce="(option) => option.value"
                :clearable="false"
                size="lg"
                placeholder="Select subtype"
                :error="displayFirstError(validate.conversion_subtype)"
            />
        </div>

        <div>
            <vx-label>Category (optional)</vx-label>
            <vx-input
                data-test="conversion-category"
                v-model="data.conversion_category"
                size="lg"
                type="text"
                placeholder="e.g., electronics, consulting, premium"
                :maxlength="80"
                :error="displayFirstError(validate.conversion_category)"
            />
        </div>

        <div>
            <vx-label>Dollar Amount</vx-label>
            <vx-input
                data-test="conversion-value"
                v-model="editingValue"
                size="lg"
                type="number"
                placeholder="0.00"
                :step="0.01"
                :min="0"
                :max="21474836.47"
                @blur="formatEditingValue"
                :error="displayFirstError(validate.conversion_value)"
            />
        </div>
    </div>
</template>

<script setup>
import { VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, required, minValue, maxValue, integer, maxLength } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { displayFirstError } from '~/utils/validation';

const data = defineModel();

if (data.value.conversion_type === undefined) {
    data.value.conversion_type = null;
}

if (data.value.conversion_subtype === undefined) {
    data.value.conversion_subtype = 'none';
}

if (data.value.conversion_category === undefined) {
    data.value.conversion_category = null;
}

if (data.value.conversion_value === undefined) {
    data.value.conversion_value = 0;
}

const typeOptions = [
    { value: 'order', label: 'Order' },
    { value: 'membership', label: 'Membership' },
    { value: 'engagement', label: 'Engagement' },
];

const subtypeOptions = computed(() => {
    switch (data.value.conversion_type) {
        case 'order':
            return [
                { value: 'none', label: 'None' },
                { value: 'one_off', label: 'One-Off' },
                { value: 'subscription', label: 'Subscription' },
            ];
        case 'membership':
            return [{ value: 'none', label: 'None' }];
        case 'engagement':
            return [
                { value: 'none', label: 'None' },
                { value: 'appointment', label: 'Appointment' },
                { value: 'reservation', label: 'Reservation' },
                { value: 'registration', label: 'Registration' },
            ];
        default:
            return [{ value: 'none', label: 'None' }];
    }
});

watch(
    () => data.value.conversion_type,
    () => {
        const validSubtypes = subtypeOptions.value.map((opt) => opt.value);
        if (!validSubtypes.includes(data.value.conversion_subtype)) {
            data.value.conversion_subtype = 'none';
        }
    }
);

watch(
    () => data.value.conversion_category,
    (value) => {
        if ('' === value) {
            data.value.conversion_category = null;
        }
    }
);

const editingValue = ref(((data.value.conversion_value || 0) / 100).toFixed(2));

const formatEditingValue = () => {
    const value = parseFloat(editingValue.value);
    if (!isNaN(value) && value >= 0) {
        editingValue.value = value.toFixed(2);
    } else {
        editingValue.value = '0.00';
    }
};

watch(editingValue, (dollars) => {
    const parsed = parseFloat(dollars);
    if (!isNaN(parsed)) {
        data.value.conversion_value = Math.round(parsed * 100);
    } else {
        data.value.conversion_value = 0;
    }
});

const csvInjectionValidator = (value) => {
    if (null === value || '' === value) {
        return true;
    }
    return !/^[@=\-+]/.test(String(value).trim());
};

const validate = useVuelidate(
    {
        conversion_type: {
            required: helpers.withMessage('Conversion type is required', required),
        },
        conversion_subtype: {
            required: helpers.withMessage('Conversion subtype is required', required),
        },
        conversion_category: {
            maxLength: helpers.withMessage('Category must be 80 characters or less', maxLength(80)),
            csvInjection: helpers.withMessage(
                'Category cannot start with @, =, -, or +',
                csvInjectionValidator
            ),
        },
        conversion_value: {
            required: helpers.withMessage('Conversion value is required', required),
            integer: helpers.withMessage('Value must be a whole number of cents', integer),
            minValue: helpers.withMessage('Value must be at least 0', minValue(0)),
            maxValue: helpers.withMessage('Value must not exceed $21,474,836.47', maxValue(2147483647)),
        },
    },
    data
);
</script>
